/* eslint-disable max-params */
import * as Sentry from '@sentry/nextjs';
import { notification } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useImmer } from 'use-immer';

import { calendarDayViewConfig } from '@/organisms/booking-calendar/config';
import { useIsSameExternalDaypassOption } from '@core/settings/actions';

import {
  bookDayPassAPICall,
  bookMeetingRoomAPICall,
  getMeetingRoomBookingCost,
  getDayPassBookingCost,
  getFreePeriods,
  getLocationPaymentMethods,
  getUnitsByLocationId,
  getAllowedOfficesByLocationId,
  getAvailablePackages,
  buyCreditAPICall,
} from './booking.service';
import {
  BookingCost,
  BOOKING_TYPE,
  FreePeriods,
  Location,
  Unit,
  PACKAGE_TYPE,
  Package,
  DaypassOffices,
} from './booking.types';

// TODO refactor this one.
const formatBookingPayload = (
  bookingInfo: any,
  selectedDate: any,
  personalInfo: any,
  paymentInfo: any,
  selectedLocationId: any,
  type: string,
  selectedLocation?: Location,
  officeId?: any,
  selectedPackageId?: any,
) => {
  const expireMonth = paymentInfo.expireDateMonth;
  const expireYear = `${paymentInfo.expireDateYear}`;
  const defaultPayload = {
    member_info: {
      company: personalInfo.company,
      ein: personalInfo.ein,
      email: personalInfo.email,
      name: personalInfo.name,
      phonenumber: personalInfo.phoneCode + personalInfo.phone,
    },
    payment_info: {
      cardNumber: paymentInfo.cardNumber,
      cvc: paymentInfo.cvv,
      expiraryDate: expireMonth + expireYear,
      expiraryMonth: expireMonth,
      expiraryYear: expireYear,
      memberName: paymentInfo.nameOnTheCard,
      paymentType: 'CC',
      zip: paymentInfo.zip,
      billingAddress: paymentInfo.address,
      save_cc: paymentInfo.save_cc,
    },
    booking_info: paymentInfo.address,
    packageId: selectedPackageId,
    locationId: selectedLocationId,
  };
  if (type !== 'packages') {
    delete defaultPayload.packageId;
    delete defaultPayload.locationId;
  } else {
    return defaultPayload;
  }
  if (type === 'meeting_room') {
    defaultPayload.booking_info = {
      conferenceRoom: bookingInfo.conferenceRoom,
      date: selectedDate.format('YYYY-MM-DD'),
      fromTime: bookingInfo.fromTime,
      toTime: bookingInfo.toTime,
      countryCode: selectedLocation?.countryCode,
      currency: selectedLocation?.currency,
      location: selectedLocationId,
    };
  } else if (type === 'day_pass') {
    if (bookingInfo.startDate && bookingInfo.endDate) {
      const startDate = dayjs(bookingInfo.startDate);
      const endDate = dayjs(bookingInfo.endDate);
      const differenceInDays = endDate.diff(startDate, 'day');
      defaultPayload.booking_info = {
        quantity: differenceInDays,
        location: selectedLocationId,
        officeId,
        startDate: startDate.format('YYYY-MM-DD'),
        endDate: endDate.format('YYYY-MM-DD'),
      };
    } else {
      defaultPayload.booking_info = {
        quantity: Number(bookingInfo.quantity),
        location: selectedLocationId,
        officeId,
        date: bookingInfo.dates,
      };
    }
  }
  return defaultPayload;
};
const paymentInfoInitialState = {
  paymentType: 'credit_card',
  cardNumber: '',
  nameOnTheCard: '',
  cvv: '',
  expireDateMonth: '',
  expireDateYear: '',
  address: '',
  zip: '',
  save_cc: true,
};

const dayPassInfoInitialState = {
  quantity: 1,
  dates: [],
  startDate: null,
  endDate: null,
};

const bookingInfoInitialState = {
  conferenceRoom: '',
  fromTime: 0,
  toTime: 0,
  uiPosition: {
    date: null,
    title: '',
    startTime: '',
    endTime: '',
    row: 0,
    col: 0,
    step: 0,
    height: 0,
    maxAvailableTime: 0,
    minAvailableTime: 0,
  },
};

const personalInfoInitialState = {
  name: '',
  email: '',
  phone: '',
  company: '',
  ein: '',
  phoneCode: '1',
};

export const useBookDayPass = () => {
  const { t } = useTranslation();
  const { query } = useRouter();
  const phpURL = useMemo(() => {
    return `https://${query.clientName}.pickspace.com/api/app`;
  }, [query.clientName]);
  const isExternalDayPassOptionFlex = useIsSameExternalDaypassOption('flex');

  const { HOUR_STEP, ROW_HEIGHT } = calendarDayViewConfig;
  const [bookingType, setBookingType] = useState<BOOKING_TYPE>(BOOKING_TYPE.MEETING_ROOM);
  const [loadingUnits, setLoadingUnits] = useState(false);
  const [loadingPackages, setLoadingPackages] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [bookMeetingRoomLoading, setBookMeetingRoomLoading] = useState(false);
  const [availablePaymentMethods, setAvailablePaymentMethods] = useState<string[]>([]);
  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs());
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);
  const [selectedUnitId, setSelectedUnitId] = useState<string | null>(null);
  const [filteredUnits, setFilteredUnits] = useState<Unit[]>([]);
  const [currentSelectedLocationUnits, setCurrentSelectedLocationUnits] = useState<Unit[]>([]);
  const [allowedOffices, setAllowedOffices] = useState<DaypassOffices[]>([]);
  const [bookingCost, setBookingCost] = useState<BookingCost>({
    originalCost: 0,
    subTotal: 0,
    total: 0,
    vat: 0,
    currency: '',
  });
  const [freePeriods, setFreePeriods] = useState<FreePeriods[]>([{}]);
  const [activeDiscountCode, setActiveDiscountCode] = useState<string>('');
  const [availablePackages, setAvailablePackages] = useState<Package[]>([]);
  const [availableAnnualPackages, setAvailableAnnualPackages] = useState<Package[]>([]);
  const [availableFloatingPackages, setAvailableFloatingPackages] = useState<Package[]>([]);
  const [selectedPackageType, setSelectedPackageType] = useState<PACKAGE_TYPE>(PACKAGE_TYPE.ANNUAL);
  const [selectedPackageId, setSelectedPackageId] = useState<number | null>(null);
  const [paymentInfo, setPaymentInfo] = useImmer({ ...paymentInfoInitialState });
  const [dayPassInfo, setDayPassInfo] = useImmer({ ...dayPassInfoInitialState });
  const [bookingInfo, setBookingInfo] = useImmer({ ...bookingInfoInitialState });
  const [personalInfo, setPersonalInfo] = useImmer({ ...personalInfoInitialState });
  const memoizedFilteredUnitsStringify = useMemo(() => JSON.stringify(filteredUnits), [filteredUnits]);
  const memoizedAllUnitsStringify = useMemo(
    () => JSON.stringify(currentSelectedLocationUnits),
    [currentSelectedLocationUnits],
  );
  const memoizedPaymentInfoStringify = useMemo(() => JSON.stringify(paymentInfo), [paymentInfo]);
  const memoizedDayPassInfoStringify = useMemo(() => JSON.stringify(dayPassInfo), [dayPassInfo]);
  const memoizedBookingInfoStringify = useMemo(() => JSON.stringify(bookingInfo), [bookingInfo]);
  const memoizedPersonalInfoStringify = useMemo(() => JSON.stringify(personalInfo), [personalInfo]);
  const memoizedFreePeriodsStringify = useMemo(() => JSON.stringify(freePeriods), [freePeriods]);
  const memoizedAvailableAnnualPackagesStringify = useMemo(
    () => JSON.stringify(availableAnnualPackages),
    [availableAnnualPackages],
  );
  const memoizedAvailableFloatingPackagesStringify = useMemo(
    () => JSON.stringify(availableFloatingPackages),
    [availableFloatingPackages],
  );

  const clearCost = useCallback(() => {
    setBookingCost({
      originalCost: 0,
      subTotal: 0,
      total: 0,
      vat: 0,
      currency: '',
    });
  }, []);

  const initBooking = useCallback(
    (roomIndex, col, step, has12Hours = true) => {
      if (bookingInfo.uiPosition.maxAvailableTime) {
        notification.error({
          message: t('booking.errorTitle'),
          description: t('booking.maxOneBooking'),
        });
        return;
      }
      const fromTime = (col - 1) * 60 + step * (60 / HOUR_STEP);
      const availableTime = freePeriods?.[roomIndex]?.[fromTime] || [];
      const selectedRoom = currentSelectedLocationUnits[roomIndex];
      const minBookedInMins = parseFloat(selectedRoom.min_booked) * 60;

      if (fromTime + minBookedInMins > availableTime[availableTime.length - 1]) {
        notification.error({
          message: t('booking.errorTitle'),
          description: t('booking.minimumBookingTime', { hours: selectedRoom.min_booked }),
        });
        return;
      }

      setBookingInfo((draft) => {
        draft.conferenceRoom = selectedRoom?.id;
        draft.fromTime = fromTime;
        draft.toTime = fromTime + minBookedInMins || 60 / HOUR_STEP;
        draft.uiPosition = {
          date: selectedDate,
          title: selectedRoom?.name || '',
          startTime: dayjs(fromTime * 60 * 1000)
            .tz('Africa/Cairo')
            .subtract(2, 'h')
            .format(has12Hours ? 'hh:mm A' : 'HH:mm'),
          endTime: dayjs((fromTime + minBookedInMins || 60 / HOUR_STEP) * 60 * 1000)
            .tz('Africa/Cairo')
            .subtract(2, 'h')
            .format(has12Hours ? 'hh:mm A' : 'HH:mm'),
          row: roomIndex,
          col,
          step,
          height: minBookedInMins ? (minBookedInMins * ROW_HEIGHT) / 60 : ROW_HEIGHT / HOUR_STEP,
          maxAvailableTime: availableTime[availableTime.length - 1] - fromTime,
          minAvailableTime: minBookedInMins,
        };
      });
    },
    [
      memoizedFilteredUnitsStringify,
      memoizedFreePeriodsStringify,
      selectedDate,
      bookingInfo.uiPosition.maxAvailableTime,
      t,
    ],
  );

  const formatTime = useCallback((minutes: number, has12Hours: boolean): string => {
    return dayjs(minutes * 60 * 1000)
      .tz('Africa/Cairo')
      .subtract(2, 'h')
      .format(has12Hours ? 'hh:mm A' : 'HH:mm');
  }, []);

  const selectTimeSlot = useCallback(
    (roomIndex: number, startMinutes: number, durationMinutes: number, has12Hours: boolean) => {
      const selectedRoom = currentSelectedLocationUnits[roomIndex];
      if (!selectedRoom) return;
      const availableTime = freePeriods?.[roomIndex]?.[startMinutes] || [];
      const maxAvail = availableTime.length ? availableTime[availableTime.length - 1] - startMinutes : 0;

      setBookingInfo((draft) => {
        draft.conferenceRoom = selectedRoom.id;
        draft.fromTime = startMinutes;
        draft.toTime = startMinutes + durationMinutes;
        draft.uiPosition = {
          date: selectedDate,
          title: selectedRoom.name || '',
          startTime: formatTime(startMinutes, has12Hours),
          endTime: formatTime(startMinutes + durationMinutes, has12Hours),
          row: roomIndex,
          col: Math.floor(startMinutes / 60) + 1,
          step: (startMinutes % 60) / 15,
          height: (durationMinutes * ROW_HEIGHT) / 60,
          maxAvailableTime: maxAvail,
          minAvailableTime: parseFloat(selectedRoom.min_booked) * 60,
        };
      });
    },
    [memoizedFilteredUnitsStringify, memoizedFreePeriodsStringify, selectedDate, formatTime],
  );

  const updateBookingHeight = useCallback(
    (height) => {
      setBookingInfo((draft) => {
        draft.uiPosition.height = height;
      });
    },
    [bookingInfo.uiPosition.height],
  );

  const deleteBooking = useCallback(() => {
    setBookingInfo((draft) => {
      draft.conferenceRoom = '';
      draft.fromTime = 0;
      draft.toTime = 0;
      draft.uiPosition = {
        date: null,
        title: '',
        startTime: '',
        endTime: '',
        row: 0,
        col: 0,
        step: 0,
        height: 0,
        maxAvailableTime: 0,
        minAvailableTime: 0,
      };
    });
    clearCost();
  }, []);

  const updateBookingEndTime = useCallback((time, has12Hours) => {
    setBookingInfo((draft) => {
      draft.uiPosition.endTime = dayjs((draft.fromTime + time) * 60 * 1000)
        .tz('Africa/Cairo')
        .subtract(2, 'h')
        .format(has12Hours ? 'hh:mm A' : 'HH:mm');
      draft.toTime = draft.fromTime + time;
    });
  }, []);

  const handlePaymentInfoInputChange = useCallback((e) => {
    setPaymentInfo((draft) => {
      draft[e.target.name] = e.target.value;
    });
  }, []);

  const handlePaymentTypeChange = useCallback((type) => {
    setPaymentInfo((draft) => {
      draft.paymentType = type;
    });
  }, []);

  const handlePersonalInfoInputChange = useCallback((e) => {
    setPersonalInfo((draft) => {
      draft[e.target.name] = e.target.value;
    });
  }, []);

  const handleDayPassInfoInputChange = useCallback((e) => {
    setDayPassInfo((draft) => {
      draft[e.target.name] = e.target.value;
    });
  }, []);

  const handleChangeDate = useCallback((index, date) => {
    setDayPassInfo((draft) => {
      draft.dates[index] = date;
    });
  }, []);

  const bookMeetingRoom = useCallback(
    (selectedLocation: Location) => {
      setBookMeetingRoomLoading(true);
      setErrorMsg('');

      bookMeetingRoomAPICall(
        phpURL,
        formatBookingPayload(
          bookingInfo,
          selectedDate,
          personalInfo,
          paymentInfo,
          selectedLocationId,
          'meeting_room',
          selectedLocation,
        ),
      )
        .then(() => {
          setShowSuccessModal(true);
        })
        .catch((err) => {
          setErrorMsg(err.message);
        })
        .finally(() => {
          setBookMeetingRoomLoading(false);
        });
    },
    [memoizedPaymentInfoStringify, memoizedBookingInfoStringify, memoizedPersonalInfoStringify],
  );

  const bookDayPass = useCallback(() => {
    setBookMeetingRoomLoading(true);
    setErrorMsg('');
    bookDayPassAPICall(
      phpURL,
      formatBookingPayload(
        dayPassInfo,
        selectedDate,
        personalInfo,
        paymentInfo,
        selectedLocationId,
        'day_pass',
        undefined,
        selectedUnitId,
      ),
    )
      .then(() => {
        setShowSuccessModal(true);
      })
      .catch((err) => {
        setErrorMsg(err.message);
      })
      .finally(() => {
        setBookMeetingRoomLoading(false);
      });
  }, [
    memoizedPaymentInfoStringify,
    memoizedBookingInfoStringify,
    memoizedPersonalInfoStringify,
    memoizedDayPassInfoStringify,
  ]);

  const buyPackage = useCallback(() => {
    setBookMeetingRoomLoading(true);
    setErrorMsg('');
    buyCreditAPICall(
      phpURL,
      formatBookingPayload(
        dayPassInfo,
        selectedDate,
        personalInfo,
        paymentInfo,
        selectedLocationId,
        'packages',
        undefined,
        undefined,
        selectedPackageId,
      ),
    )
      .then(() => {
        setShowSuccessModal(true);
      })
      .catch((err) => {
        setErrorMsg(err.message);
      })
      .finally(() => {
        setBookMeetingRoomLoading(false);
      });
  }, [selectedLocationId, selectedPackageId, selectedPackageType, memoizedPaymentInfoStringify]);

  const clearBookingForm = useCallback(() => {
    setBookingInfo((draft) => {
      draft = { ...bookingInfoInitialState };
      return draft;
    });
    setPersonalInfo((draft) => {
      draft = { ...personalInfoInitialState };
      return draft;
    });
    setPaymentInfo((draft) => {
      draft = { ...paymentInfoInitialState };
      return draft;
    });
    setDayPassInfo((draft) => {
      draft = { ...dayPassInfoInitialState };
      return draft;
    });
  }, []);

  const updateFreePeriods = () => {
    getFreePeriods(
      phpURL,
      selectedDate.format('YYYY-MM-DD'),
      currentSelectedLocationUnits.map((unit) => unit.id),
    )
      .catch((err) => {
        setErrorMsg(err.message);
      })
      .then((res) => {
        setFreePeriods(res as FreePeriods[]);
      });
  };

  useEffect(() => {
    setSelectedPackageId(null);
    clearCost();
    if (selectedLocationId && selectedPackageType) {
      setLoadingPackages(true);
      getAvailablePackages(phpURL, selectedLocationId, 'getAnnualPackages').then((res) => {
        setAvailableFloatingPackages(res?.packages || []);
      });
      getAvailablePackages(phpURL, selectedLocationId, 'getFloatingPackages')
        .then((res) => {
          setAvailableAnnualPackages(res?.packages || []);
        })
        .finally(() => {
          setLoadingPackages(false);
        });
    }
  }, [selectedLocationId, selectedPackageType]);

  useEffect(() => {
    if (selectedPackageType === PACKAGE_TYPE.FLOATING) {
      setAvailablePackages(availableFloatingPackages);
    } else if (selectedPackageType === PACKAGE_TYPE.ANNUAL) {
      setAvailablePackages(availableAnnualPackages);
    }
  }, [selectedPackageType, memoizedAvailableAnnualPackagesStringify, memoizedAvailableFloatingPackagesStringify]);

  useEffect(() => {
    if (selectedPackageId) {
      const selectedPackage = availablePackages.find((packageItem) => packageItem.id === selectedPackageId);
      setBookingCost({
        originalCost: selectedPackage.credit_price,
        subTotal: selectedPackage.credit_price,
        total: selectedPackage.total_price,
        vat: selectedPackage.vat,
        currency: selectedPackage.currency_symbol,
      });
    }
  }, [selectedPackageId]);

  useEffect(() => {
    if (selectedUnitId) {
      const found = currentSelectedLocationUnits.find((unit) => unit.id === selectedUnitId);
      setFilteredUnits(found ? [found] : []);
    } else {
      setFilteredUnits([]);
    }
  }, [selectedUnitId, currentSelectedLocationUnits]);

  useEffect(() => {
    if (selectedLocationId) {
      getLocationPaymentMethods(phpURL, selectedLocationId)
        .then((res) => {
          setAvailablePaymentMethods(res);
          const hasCreditCard = res.find((method) => method === 'credit_card');
          if (!hasCreditCard) {
            setPaymentInfo((draft) => {
              draft = { ...paymentInfoInitialState };
              return draft;
            });
          }
          setPaymentInfo((draft) => {
            [draft.paymentType] = res;
          });
        })
        .catch((err) => {
          setErrorMsg(err.message);
        });
      if (!isExternalDayPassOptionFlex && bookingType === BOOKING_TYPE.DAY_PASS) {
        getAllowedOfficesByLocationId(phpURL, selectedLocationId, dayPassInfo.startDate, dayPassInfo.endDate)
          .then((res) => {
            setAllowedOffices(res || []);
            setSelectedUnitId(undefined);
            clearCost();
          })
          .catch((err) => {
            setErrorMsg(err.message);
          })
          .finally(() => {
            setLoadingUnits(false);
          });
      }
      if (bookingType === BOOKING_TYPE.MEETING_ROOM) {
        setLoadingUnits(true);
        getUnitsByLocationId(phpURL, selectedLocationId)
          .then((res) => {
            deleteBooking();
            setCurrentSelectedLocationUnits(res);
            setFilteredUnits([]);
            setSelectedUnitId(undefined);
          })
          .catch((err) => {
            setErrorMsg(err.message);
            Sentry.captureException(err);
          })
          .finally(() => {
            setLoadingUnits(false);
          });
      }
    }
  }, [selectedLocationId, dayPassInfo.startDate, dayPassInfo.endDate]);

  useEffect(() => {
    if (dayPassInfo.quantity && selectedLocationId && bookingType === BOOKING_TYPE.DAY_PASS) {
      let { quantity } = dayPassInfo;
      if (!isExternalDayPassOptionFlex && dayPassInfo.endDate && dayPassInfo.startDate) {
        quantity = dayjs(dayPassInfo.endDate).diff(dayPassInfo.startDate, 'day') + 1;
      }
      getDayPassBookingCost(phpURL, {
        quantity,
        locationId: selectedLocationId,
        officeId: selectedUnitId,
      })
        .catch((err) => {
          setErrorMsg(err.message);
          Sentry.captureException(err);
        })
        .then((cost) => {
          setBookingCost(cost as BookingCost);
        });
    }
  }, [
    dayPassInfo.quantity,
    dayPassInfo.startDate,
    dayPassInfo.endDate,
    selectedUnitId,
    dayPassInfo,
    selectedLocationId,
    bookingType,
    isExternalDayPassOptionFlex,
    phpURL,
  ]);

  useEffect(() => {
    if (
      bookingInfo.conferenceRoom &&
      bookingInfo.toTime &&
      selectedLocationId &&
      bookingType === BOOKING_TYPE.MEETING_ROOM
    ) {
      getMeetingRoomBookingCost(phpURL, {
        conferenceRoom: bookingInfo.conferenceRoom,
        date: selectedDate.format('YYYY-MM-DD'),
        fromTime: bookingInfo.fromTime,
        toTime: bookingInfo.toTime,
        discount_code: activeDiscountCode || undefined,
      })
        .catch((err) => {
          setErrorMsg(err.message);
          Sentry.captureException(err);
        })
        .then((response: any) => {
          if (response.status === 'failed') {
            notification.error({
              message: t('booking.errorTitle'),
              description: response.message,
            });
          }
          setBookingCost(response as BookingCost);
        });
    }
  }, [
    bookingInfo.conferenceRoom,
    bookingInfo.fromTime,
    bookingInfo.toTime,
    selectedLocationId,
    bookingType,
    selectedDate,
    phpURL,
    activeDiscountCode,
    t,
  ]);

  useEffect(() => {
    if (bookingType === BOOKING_TYPE.MEETING_ROOM && currentSelectedLocationUnits.length) {
      updateFreePeriods();
    }
  }, [selectedDate, bookingType, memoizedAllUnitsStringify, showSuccessModal]);

  useEffect(() => {
    if (showSuccessModal) {
      updateFreePeriods();
    }
  }, [showSuccessModal]);

  useEffect(() => {
    const selectedOffice = allowedOffices.find((unit) => unit.id === selectedUnitId);
    setDayPassInfo((draft) => {
      if (!isExternalDayPassOptionFlex && selectedOffice) {
        draft.dates = [dayjs(selectedOffice?.valid_date).add(1, 'day').format('YYYY-MM-DD')];
      } else {
        if (dayPassInfo.quantity === 0) {
          draft.dates.push(dayjs().add(1, 'day').format('YYYY-MM-DD'));
        }
        const newQuantity = Number(draft.quantity - draft.dates.length);
        for (let i = 0; i < newQuantity; i++) {
          draft.dates.push(
            dayjs(draft.dates[draft.dates.length - 1])
              .add(1, 'day')
              .format('YYYY-MM-DD'),
          );
        }
        if (newQuantity < 0) draft.dates = draft.dates.slice(0, draft.quantity);
      }
    });
  }, [dayPassInfo.quantity, selectedUnitId]);

  const applyDiscountCode = useCallback(
    (discountCode: string) => {
      if (!bookingInfo.conferenceRoom || !bookingInfo.toTime) return;
      setActiveDiscountCode(discountCode);
      getMeetingRoomBookingCost(phpURL, {
        conferenceRoom: bookingInfo.conferenceRoom,
        date: selectedDate.format('YYYY-MM-DD'),
        fromTime: bookingInfo.fromTime,
        toTime: bookingInfo.toTime,
        discount_code: discountCode,
      })
        .then((response: any) => {
          if (response.status === 'failed') {
            notification.error({
              message: t('booking.errorTitle'),
              description: response.message,
            });
          }
          setBookingCost(response as BookingCost);
        })
        .catch((err) => {
          setErrorMsg(err.message);
          Sentry.captureException(err);
        });
    },
    [bookingInfo.conferenceRoom, bookingInfo.fromTime, bookingInfo.toTime, selectedDate, phpURL, t],
  );

  return {
    selectedLocationId,
    selectedDate,
    loadingUnits,
    filteredUnits,
    currentSelectedLocationUnits,
    personalInfo,
    bookingInfo,
    selectedUnitId,
    freePeriods,
    bookingCost,
    dayPassInfo,
    paymentInfo,
    availablePaymentMethods,
    bookMeetingRoomLoading,
    showSuccessModal,
    errorMsg,
    allowedOffices,
    availablePackages,
    selectedPackageType,
    selectedPackageId,
    loadingPackages,
    setBookingType,
    setSelectedLocationId,
    setSelectedDate,
    handlePersonalInfoInputChange,
    initBooking,
    selectTimeSlot,
    formatTime,
    setSelectedUnitId,
    updateBookingHeight,
    deleteBooking,
    updateBookingEndTime,
    handlePaymentInfoInputChange,
    handlePaymentTypeChange,
    handleDayPassInfoInputChange,
    handleChangeDate,
    bookMeetingRoom,
    bookDayPass,
    buyPackage,
    setShowSuccessModal,
    clearBookingForm,
    setAvailablePackages,
    setSelectedPackageType,
    setSelectedPackageId,
    applyDiscountCode,
  };
};
